import React from 'react';

// "Up next" at the end of a story or episode, in Pixley's own look: a paper
// card with Pip peeking over its top edge (rises up, tilts his head, blinks,
// hops when it's almost time) saying what's next, the
// next story's picture taped on like a sticker, and the Play now button
// filling up as it counts down.

export const UP_NEXT_SECONDS = 8;


const PIP_LINES = {
  library: 'Next up from your library!',
  recommended: 'Ooh, I picked this one for you!',
  episode: "Next episode, let's go!",
  story: 'Here comes another story!',
};

const css = `
@keyframes pxNextPop {
  0%   { opacity: 0; transform: translateY(24px) scale(0.9) rotate(-1.5deg); }
  60%  { opacity: 1; transform: translateY(-4px) scale(1.02) rotate(0.4deg); }
  100% { opacity: 1; transform: translateY(0) scale(1) rotate(0deg); }
}
@keyframes pxNextPeek {
  0%   { transform: translateY(105%); }
  100% { transform: translateY(0); }
}
@keyframes pxNextTilt {
  0%, 100% { transform: rotate(0deg); }
  30%      { transform: rotate(-5deg); }
  60%      { transform: rotate(3deg); }
}
@keyframes pxNextBlink { 0%, 92%, 100% { opacity: 0; } 94%, 98% { opacity: 1; } }
@keyframes pxNextHop {
  0%, 100% { transform: translateY(0) scale(1); }
  40%      { transform: translateY(-9px) scale(0.97, 1.04); }
  75%      { transform: translateY(0) scale(1.04, 0.95); }
}
@keyframes pxNextBubble {
  0%, 35% { opacity: 0; transform: scale(0.6); }
  70%     { opacity: 1; transform: scale(1.06); }
  100%    { opacity: 1; transform: scale(1); }
}
@keyframes pxNextTwinkle {
  0%, 100% { opacity: 0.35; transform: scale(0.8) rotate(0deg); }
  50%      { opacity: 1; transform: scale(1.1) rotate(20deg); }
}
@keyframes pxNextScrim { from { opacity: 0; } to { opacity: 1; } }
.pxn-card   { animation: pxNextPop 560ms cubic-bezier(0.22, 1, 0.36, 1) both; }
.pxn-pip    { animation: pxNextPeek 720ms 180ms cubic-bezier(0.34, 1.4, 0.64, 1) both; }
.pxn-tilt   { transform-origin: 50% 100%; animation: pxNextTilt 3.4s 1s ease-in-out infinite; }
.pxn-blink  { animation: pxNextBlink 3.8s 1.4s infinite; opacity: 0; }
.pxn-hop    { animation: pxNextHop 420ms cubic-bezier(0.34, 1.56, 0.64, 1); }
.pxn-bubble { animation: pxNextBubble 700ms 200ms cubic-bezier(0.22, 1, 0.36, 1) both; transform-origin: 0% 100%; }
.pxn-star   { animation: pxNextTwinkle 2.2s ease-in-out infinite; transform-origin: center; }
.pxn-scrim  { animation: pxNextScrim 400ms ease both; }
.pxn-sticker { transition: transform 200ms ease; }
.pxn-sticker:hover { transform: rotate(0deg) scale(1.02) !important; }
.pxn-sticker:hover .pxn-play { transform: scale(1.08); }
.pxn-sticker:focus-visible, .pxn-btn:focus-visible { outline: 3px solid #7FC6FF; outline-offset: 4px; }
.pxn-btn-go { transition: transform 120ms ease, box-shadow 120ms ease; }
.pxn-btn-go:active { transform: translateY(3px); box-shadow: 0 1px 0 #073B8E !important; }
@media (prefers-reduced-motion: reduce) {
  .pxn-card, .pxn-pip, .pxn-tilt, .pxn-blink, .pxn-hop, .pxn-bubble, .pxn-star, .pxn-scrim { animation: none; }
  .pxn-fill { transition: none !important; }
}
`;

const Star = ({ x, y, size, color, delay }) => (
  <svg
    className="pxn-star"
    aria-hidden="true"
    viewBox="0 0 24 24"
    width={size}
    height={size}
    style={{ position: 'absolute', left: x, top: y, animationDelay: `${delay}s`, pointerEvents: 'none' }}
  >
    <path
      d="M12 2l2.6 6.6L21 9.2l-5 4.6 1.4 7L12 17.3 6.6 20.8 8 13.8 3 9.2l6.4-.6z"
      fill={color}
      stroke="#0B1A33"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

export default function UpNextCard({
  seconds,
  total = UP_NEXT_SECONDS,
  variant = 'story', // library | recommended | episode | story
  title,
  thumbnail,
  onPlay,
  onCancel,
  size = 'large', // 'large' (full-screen player) | 'medium' (inline player)
}) {
  const large = size === 'large';
  const progress = Math.max(0, Math.min(1, (total - seconds + 1) / total));
  // Inline player: a wide, short card (picture left, words right) so the whole
  // moment, Pip included, fits inside a 16:9 frame. Full-screen: stacked.
  const row = !large;
  // Pip peeking over the card: his paws hang a little over the top edge.
  const peekH = large ? 112 : 84;
  const peekW = peekH * (251 / 240);
  const overlap = Math.round(peekH * 0.16);
  const pipLeft = large ? 26 : 18;
  const stop = (fn) => (e) => {
    e.stopPropagation();
    fn();
  };

  return (
    <>
      <style>{css}</style>
      <div
        className="pxn-scrim"
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 29,
          pointerEvents: 'none',
          background: 'linear-gradient(0deg, rgba(11,26,51,0.62) 0%, rgba(11,26,51,0.28) 55%, rgba(11,26,51,0.12) 100%)',
        }}
      />
      <div
        role="status"
        aria-live="polite"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          zIndex: 30,
          right: large ? 'clamp(16px, 4vw, 56px)' : 'clamp(10px, 3%, 28px)',
          bottom: large ? 'clamp(96px, 15vh, 150px)' : 'clamp(58px, 11%, 80px)',
          width: large ? 'min(540px, calc(100% - 32px))' : 'clamp(360px, 64%, 640px)',
          paddingTop: peekH - overlap,
        }}
      >
        {/* Pip, peeking over the card's top edge */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: pipLeft - 10,
            top: -12,
            width: peekW + 20,
            height: peekH + 12,
            overflow: 'hidden',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        >
          <div className="pxn-pip" style={{ position: 'absolute', left: 10, bottom: 0, width: peekW, height: peekH }}>
            <div key={seconds <= 3 ? `hop-${seconds}` : 'calm'} className={seconds <= 3 ? 'pxn-hop' : undefined} style={{ width: '100%', height: '100%' }}>
              <div className="pxn-tilt" style={{ position: 'relative', width: '100%', height: '100%', filter: 'drop-shadow(0 3px 4px rgba(11,26,51,0.22))' }}>
                <img src="/assets/player/pip_peek.webp" alt="" draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
                <img className="pxn-blink" src="/assets/player/pip_peek_blink.webp" alt="" draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
              </div>
            </div>
          </div>
        </div>

        {/* What Pip says */}
        <div
          className="pxn-bubble"
          style={{
            position: 'absolute',
            left: pipLeft + peekW + 4,
            top: Math.round(peekH * 0.12),
            zIndex: 3,
            maxWidth: `calc(100% - ${pipLeft + peekW + 12}px)`,
            background: '#FFFDF7',
            color: '#0065F8',
            fontFamily: "'Fredoka', 'Inter', sans-serif",
            fontWeight: 600,
            fontSize: large ? 19 : 16,
            lineHeight: 1.15,
            padding: large ? '8px 16px 10px' : '6px 14px 8px',
            borderRadius: 18,
            border: '2.5px solid #0B1A33',
            boxShadow: '0 4px 0 rgba(11,26,51,0.18)',
          }}
        >
          {PIP_LINES[variant] || PIP_LINES.story}
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: -11,
              bottom: 10,
              width: 16,
              height: 16,
              background: '#FFFDF7',
              borderLeft: '2.5px solid #0B1A33',
              borderBottom: '2.5px solid #0B1A33',
              transform: 'rotate(45deg)',
              borderRadius: '0 0 0 4px',
            }}
          />
        </div>

        {/* The paper card */}
        <div
          className="pxn-card"
          style={{
            position: 'relative',
            borderRadius: 28,
            padding: large ? '22px 20px 18px' : '18px 16px 14px',
            background: 'linear-gradient(180deg, #FFFDF7 0%, #F5FAFF 55%, #EAF3FF 100%)',
            border: '2.5px solid #0B1A33',
            boxShadow: '0 6px 0 #0B1A33, 0 26px 60px rgba(11,26,51,0.35)',
            fontFamily: "'Fredoka', 'Inter', sans-serif",
            color: '#0B1A33',
          }}
        >
          <div style={{ position: 'absolute', right: 18, top: -14 }}>
            <Star x={0} y={0} size={large ? 26 : 22} color="#FFD76A" delay={0} />
          </div>
          <div style={{ position: 'absolute', right: -12, top: 40 }}>
            <Star x={0} y={0} size={large ? 18 : 16} color="#FF6B9A" delay={0.7} />
          </div>
          <div style={{ position: 'absolute', left: -10, bottom: 70 }}>
            <Star x={0} y={0} size={large ? 16 : 14} color="#3EDC8F" delay={1.3} />
          </div>

          <div
            style={
              row
                ? { display: 'grid', gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr)', gap: 16, alignItems: 'center' }
                : undefined
            }
          >
          {/* The next story, taped on like a sticker */}
          <button
            type="button"
            className="pxn-sticker"
            onClick={stop(onPlay)}
            aria-label={`Play ${title} now`}
            style={{
              position: 'relative',
              display: 'block',
              width: '100%',
              padding: 7,
              border: '2.5px solid #0B1A33',
              borderRadius: 18,
              background: '#FFFFFF',
              cursor: 'pointer',
              transform: 'rotate(-1.4deg)',
              boxShadow: '0 4px 0 rgba(11,26,51,0.18)',
            }}
          >
            {/* washi tape */}
            <span
              aria-hidden="true"
              style={{ position: 'absolute', bottom: -10, left: 22, width: 64, height: 20, background: 'rgba(255,215,106,0.85)', transform: 'rotate(6deg)', borderRadius: 3, zIndex: 2 }}
            />
            <span
              aria-hidden="true"
              style={{ position: 'absolute', top: -10, right: 22, width: 64, height: 20, background: 'rgba(255,107,154,0.7)', transform: 'rotate(7deg)', borderRadius: 3, zIndex: 2 }}
            />
            <span
              style={{
                position: 'relative',
                display: 'block',
                aspectRatio: '16 / 9',
                borderRadius: 12,
                overflow: 'hidden',
                background: 'linear-gradient(135deg,#0065F8,#00CAFF)',
              }}
            >
              {thumbnail && (
                <img src={thumbnail} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              )}
              {/* rainbow crayon countdown around the play button */}
              <span
                className="pxn-play"
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: large ? 88 : 72,
                  height: large ? 88 : 72,
                  marginLeft: large ? -44 : -36,
                  marginTop: large ? -44 : -36,
                  transition: 'transform 200ms ease',
                }}
              >
                <svg viewBox="0 0 88 88" width="100%" height="100%">
                  <circle cx="44" cy="44" r="38" fill="#FFFDF7" stroke="#0B1A33" strokeWidth="2.5" />
                  <path d="M36 27 L61 44 L36 61 Z" fill="#0065F8" stroke="#073B8E" strokeWidth="2" strokeLinejoin="round" />
                </svg>
              </span>
            </span>
          </button>

          <div style={row ? { minWidth: 0 } : undefined}>
          <div
            style={
              row
                ? { display: 'flex', flexDirection: 'column', gap: 2, margin: '0 0 12px' }
                : { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, margin: '16px 4px 12px' }
            }
          >
            <div
              style={{
                fontSize: large ? 24 : 19,
                fontWeight: 700,
                lineHeight: 1.15,
                overflow: 'hidden',
                minWidth: 0,
                ...(row
                  ? { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }
                  : { textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
              }}
            >
              {title}
            </div>
          </div>

          <div
            style={
              row
                ? { display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'stretch' }
                : { display: 'flex', gap: 8, alignItems: 'center' }
            }
          >
            <button
              type="button"
              className="pxn-btn pxn-btn-go"
              onClick={stop(onPlay)}
              aria-label={`Play now, starts in ${seconds} seconds`}
              style={{
                position: 'relative',
                overflow: 'hidden',
                flex: row ? 'none' : 1,
                width: row ? '100%' : undefined,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                background: 'linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)',
                color: '#FFFFFF',
                border: '2.5px solid #0B1A33',
                borderRadius: 999,
                padding: large ? '12px 20px' : '10px 16px',
                fontFamily: "'Fredoka', sans-serif",
                fontSize: large ? 18 : 16,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 0 #073B8E',
              }}
            >
              {/* the countdown: a lighter blue sweeping across the button */}
              <span
                className="pxn-fill"
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${progress * 100}%`,
                  background: 'linear-gradient(180deg, #3F9BFF 0%, #1F83FF 100%)',
                  transition: 'width 1s linear',
                }}
              />
              <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
                Play now
                <span
                  aria-hidden="true"
                  style={{
                    minWidth: large ? 30 : 26,
                    height: large ? 30 : 26,
                    borderRadius: 999,
                    background: '#FFFDF7',
                    color: '#0065F8',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: large ? 16 : 14,
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {seconds}
                </span>
              </span>
            </button>
            <button
              type="button"
              className="pxn-btn"
              onClick={stop(onCancel)}
              style={{
                background: 'transparent',
                color: '#5A6B85',
                border: 'none',
                padding: large ? '12px 14px' : '10px 12px',
                fontFamily: "'Fredoka', sans-serif",
                fontSize: large ? 16 : 15,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Not now
            </button>
          </div>
          </div>
          </div>
        </div>
      </div>
    </>
  );
}
